import React from 'react';
import { observer } from 'mobx-react';

import { Footer } from '../../../common/footer';
import { HomeNavbar } from '../../../common/home-navbar';
import { BasicInput } from '../../../common/basic-input';
import { ScreenStore } from '../../../stores/ScreenStore';
import { constants } from '../../../config';
import { routeNaming, AppLink } from '../../../routes';
import { PhoneIcon, EnvelopeIcon } from './img';
import { RecaptchaComponent } from '../../../common/recaptcha-component';
import { enforceParameters } from '../../../hocs/enforce-parameters';
import { SecondaryButton } from '../../../common/secondary-button';
import { Select } from '../../../common/select';
import { ContactStore } from './stores/ContactStore';
import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';
import { SessionStore } from '../../../stores/SessionStore';
import { USER_TYPES } from '../../../models/User';
import { InformationMenu } from '../information-menu';
import styles from './Contact.css';
import { SEO } from '../../../common/seo';

class Contact extends React.Component {
  static loggedUserName() {
    let userName = null;
    if (SessionStore.user.type === USER_TYPES.COMPANY) {
      userName = SessionStore.user.tradeName;
    } else {
      userName = `${SessionStore.user.names} ${SessionStore.user.surnames}`;
    }
    return userName;
  }

  static generateReasonOptions() {
    return ConfigParametersStore.parameters.contactReasons.map(reason => ({
      label: reason.name,
      value: reason.id,
    }));
  }

  constructor(props) {
    super(props);
    this.state = {
      userName: SessionStore.loggedIn ? Contact.loggedUserName() : null,
      email: SessionStore.loggedIn ? SessionStore.user.email : null,
      selectedReason: null,
      question: null,

      recaptchaToken: null,
      recaptchaError: null,
    };

    this._updateUserName = this._updateUserName.bind(this);
    this._updateEmail = this._updateEmail.bind(this);
    this._updateSelectedReason = this._updateSelectedReason.bind(this);
    this._updateQuestion = this._updateQuestion.bind(this);
    this._onRecaptchaError = this._onRecaptchaError.bind(this);
    this._onRecaptchaExpiry = this._onRecaptchaExpiry.bind(this);
    this._onRecaptchaSuccess = this._onRecaptchaSuccess.bind(this);
    this._dataIsValid = this._dataIsValid.bind(this);
    this._submitData = this._submitData.bind(this);
  }

  componentWillUnmount() {
    ContactStore.clearSubmitStates();
  }

  _updateUserName(event) {
    const { target: { value } } = event;
    this.setState({
      userName: value,
    });
  }

  _updateEmail(event) {
    const { target: { value } } = event;
    this.setState({
      email: value,
    });
  }

  _updateSelectedReason(type) {
    this.setState({
      selectedReason: type,
    });
  }

  _updateQuestion(event) {
    const { target: { value } } = event;
    this.setState({
      question: value,
    });
  }

  _onRecaptchaSuccess(token) {
    this.setState({
      recaptchaError: false,
      recaptchaToken: token,
    });
  }

  _onRecaptchaError() {
    this.setState({
      recaptchaError: true,
    });
  }

  _onRecaptchaExpiry() {
    this.setState({
      recaptchaToken: null,
    });
  }

  _dataIsValid() {
    const dataIsValid = (
      !!this.state.userName &&
      !!this.state.email &&
      !!this.state.selectedReason &&
      !!this.state.question &&
      !!this.state.recaptchaToken
    );
    return dataIsValid;
  }

  _submitData(event) {
    event.preventDefault();
    if (!this._dataIsValid()) {
      return;
    }
    ContactStore.create({
      userName: this.state.userName,
      email: this.state.email,
      question: this.state.question,
      questionReason: this.state.selectedReason,
      recaptchaToken: this.state.recaptchaToken,
    });
  }

  render() {
    const contactReasonOptions = Contact.generateReasonOptions();
    const showMobileVersion = ScreenStore.screenWidth <= constants.breakpoints.mobileThreshold;

    const userNameError = this.state.userName === '' ? 'Este campo no puede ser vacío' : null;
    const emailError = this.state.email === '' ? 'Este campo no puede ser vacío' : null;

    const selectedReason = this.state.selectedReason !== null ?
      contactReasonOptions.find(reason => reason.value === this.state.selectedReason).label
      : 'Elija el motivo de su consulta';

    return (
      <div className={styles.container}>
        <SEO
          title="Contacto"
          description="Accede a toda la información relevante sobre Carlos Gutiérrez S.A, nuestros servicios, políticas y cómo podemos ayudarte. Encuentra detalles sobre nuestros productos, condiciones de uso, privacidad, envíos y más. Estamos comprometidos a ofrecerte la mejor experiencia, brindándote transparencia y claridad en todos nuestros procesos. Si tienes preguntas o dudas, esta sección es tu recurso principal para obtener respuestas. Descubre todo lo que necesitas saber y disfruta de una experiencia de compra segura y satisfactoria con nosotros"
          type="website"
        />
        <HomeNavbar />
        <div className={styles.mainWrapper}>
          <div className={styles.mainContent}>
            {showMobileVersion ?
              <p className={styles.breadcrumb}>
                <AppLink
                  className={styles.breadcrumb}
                  routeName={routeNaming.INFORMATION_MENU_MOBILE}
                >
                  Información
                </AppLink>
                <span> &gt; Contacto</span>
              </p>
              : <InformationMenu indexSelectedItem={1} />}
            <div className={styles.bodyContainer}>
              <div className={styles.titleContainer}>
                <h1 className={styles.mainTitle}> Contacto </h1>
              </div>
              <div className={styles.firstSection}>
                <div className={styles.iconTextContainer}>
                  <img className={styles.icon} src={PhoneIcon} alt="" />
                  <div className={styles.textContainer}>
                    <p> Consultas Telefónicas </p>
                    <p> 2902 7737 </p>
                  </div>
                </div>
                <div className={styles.iconTextContainer}>
                  <img className={styles.icon} src={EnvelopeIcon} alt="" />
                  <div className={styles.textContainer}>
                    <p> Consultas Web </p>
                    <p> info@carlosgutierrez.com.uy </p>
                  </div>
                </div>
              </div>
              <div className={styles.contentSection}>
                <h2 className={styles.contentSectionSubtitle}> Realizar una consulta on-line </h2>
                <form
                  onSubmit={this._submitData}
                >
                  <div className={styles.formContainer}>
                    <div className={styles.formColumnContainer}>
                      <BasicInput
                        className={styles.input}
                        label="Nombre"
                        name="name"
                        onChange={this._updateUserName}
                        error={userNameError}
                        value={this.state.userName || ''}
                        disabled={ContactStore.submitting}
                      />
                      <BasicInput
                        className={styles.input}
                        label="Email"
                        name="email"
                        onChange={this._updateEmail}
                        error={emailError}
                        value={this.state.email || ''}
                        disabled={ContactStore.submitting}
                      />
                      <div className={styles.selectQuestionReason}>
                        <label className={styles.selectLabel}>
                          Motivo de consulta
                        </label>
                        <Select
                          className={styles.select}
                          onChange={this._updateSelectedReason}
                          options={contactReasonOptions}
                          value={selectedReason}
                          selectedClassName={styles.selectValue}
                          disabled={ContactStore.submitting}
                        />
                      </div>
                    </div>
                    <div className={styles.questionTextAreaContainer}>
                      <label className={styles.questionLabel}>
                        Consulta
                      </label>
                      <textarea
                        className={styles.questionTextArea}
                        disabled={ContactStore.submitting}
                        onChange={this._updateQuestion}
                        placeholder="Escriba su consulta, por favor identifíquese
                          con su número de CI y nombre"
                        value={this.state.question || ''}
                      />
                    </div>
                  </div>
                  <div className={styles.submitSection}>
                    <div className={styles.recaptcha}>
                      {
                        !!this.state.recaptchaError &&
                        <p className={styles.errorMessage}>
                          Ha ocurrido un error, intente realizar el reCAPTCHA de nuevo.
                        </p>
                      }
                      <RecaptchaComponent
                        onError={this._onRecaptchaError}
                        onExpiry={this._onRecaptchaExpiry}
                        onSuccess={this._onRecaptchaSuccess}
                      />
                    </div>
                    {
                      ContactStore.submitError &&
                      <p className={styles.errorMessage}>
                        Ha ocurrido un error, por favor inténtelo nuevamente.
                      </p>
                    }
                    {
                      ContactStore.submitSuccess &&
                      <p className={styles.successMessage}>
                        Tu consulta se envió con éxito. Te contactaremos a la brevedad.
                      </p>
                    }
                    <SecondaryButton
                      className={styles.sendQueryButton}
                      type="submit"
                      disabled={!this._dataIsValid()}
                    >
                      Enviar Consulta
                    </SecondaryButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
}

const ObservedContact = enforceParameters(observer(Contact));

export { ObservedContact as Contact };



// WEBPACK FOOTER //
// ./src/pages/information/contact/Contact.jsx